import { useFlagOverrides } from "./use-flag-overrides.js";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { useTelemetryContext } from "@myphx-engineering/instrumentation/react";
import { jsx } from "react/jsx-runtime";

//#region src/client/FeatureFlagProvider.tsx
const FeatureFlagContext = createContext({
	overriddenFlags: {},
	setFlagOverride: (_flagState) => {},
	clearFlagOverrides: () => {},
	updateProviderContext: (_newContext) => {}
});
const FeatureFlagContextProvider = ({ children }) => {
	const client = useLDClient();
	const [isClientReady, setIsClientReady] = useState(false);
	const { telemetryInstance } = useTelemetryContext();
	const { logError } = telemetryInstance;
	useEffect(() => {
		if (client) setIsClientReady(true);
	}, [client]);
	const updateProviderContext = useCallback(async (newContext) => {
		if (!client || !isClientReady) {
			logError({
				source: "FeatureFlagProvider",
				exception: /* @__PURE__ */ new Error("LD client not instantiated")
			});
			return;
		}
		try {
			await client.identify(newContext);
		} catch (e) {
			logError({
				source: "FeatureFlagProvider",
				exception: e
			});
			return;
		}
	}, [
		client,
		isClientReady,
		logError
	]);
	const { clearFlagOverride, clearFlagOverrides, overriddenFlags, setFlagOverride } = useFlagOverrides();
	const values = useMemo(() => ({
		clearFlagOverride,
		clearFlagOverrides,
		overriddenFlags,
		setFlagOverride,
		updateProviderContext
	}), [
		clearFlagOverride,
		clearFlagOverrides,
		overriddenFlags,
		setFlagOverride,
		updateProviderContext
	]);
	return /* @__PURE__ */ jsx(FeatureFlagContext.Provider, {
		value: values,
		children
	});
};
const FeatureFlagProvider = ({ provider, children }) => {
	return /* @__PURE__ */ jsx(provider, { children: /* @__PURE__ */ jsx(FeatureFlagContextProvider, { children }) });
};

//#endregion
export { FeatureFlagContext, FeatureFlagProvider };